'use client'

import { useGraphQL } from 'hooks/useGraphQL/useGraphQL'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { url } from 'utils/url/url'
import { ENDPOINT_GATEWAY } from '../constants/constants'
import { isTokenError } from '../utils/exceptions'
import { useErrorManagement } from './useErrorManagement'
import {
	type GraphQlError,
	getErrorHandler,
	useHandleErrors,
} from './useHandleErrors'

type FetcherArgs<TVariables> = [query: string, variables?: Partial<TVariables>]

export const fetchUrl = url(`${ENDPOINT_GATEWAY}`)

const isGraphQLResponseError = (error: unknown): error is Error[] =>
	Array.isArray(error) && error.length > 0 && error[0].message !== undefined

export const useCartFetcher = <
	TData,
	TVariables extends Record<string, unknown>,
>() => {
	const { fetcher: fetcherGraphQL } = useGraphQL()
	const { channel } = useMasterData()
	const { handleError } = useHandleErrors()
	const {
		country: { countryISO },
	} = useMasterData()

	const defaultVariables = {
		orderBy: { createdAt: 'DESC' },
		// biome-ignore lint/style/useNamingConvention: GraphQL API property name
		countryIso3166_2: countryISO,
	}
	const { addErrorManagement } = useErrorManagement()

	async function fetcher(args: FetcherArgs<TVariables>): Promise<TData> {
		const [query, variables] = args

		const response = await fetcherGraphQL<TData, TVariables, Error>({
			query,
			url: fetchUrl,
			variables: {
				channel,
				...defaultVariables,
				...(variables ?? {}),
			} as unknown as TVariables,
		})

		if (!response) {
			throw new Error('No response received from GraphQL')
		}

		if (isGraphQLResponseError(response)) {
			if (!isTokenError(response)) {
				handleError(
					getErrorHandler(query),
					response as unknown as GraphQlError | GraphQlError[]
				)
			}

			await addErrorManagement(response, query)

			throw response
		}

		return response as TData
	}

	return {
		fetcher,
	}
}
